import { getRegionConfig } from 'config';
import { Region } from 'constants/shop';
import { Entry } from 'contentful';
import { IBlogCategoryFields } from 'global';
import {
  ArticleCard,
  fetchMagazineIndex,
  getArticleCards,
  getMagazineData,
} from 'modules/magazine/contentful';
import { getMagazineCanonicalUrl } from 'modules/magazine/canonical-url';
import usePageNumber from 'modules/magazine/hooks/use-pageNumber';
import { Pagination } from 'modules/magazine/interface';
import SubCategory from 'modules/magazine/sub-category';
import MagazineHead from 'modules/magazine/magazine-head';
import { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import { BathUrlQuery } from 'pages/[region]/[magazine]';
import { ParsedUrlQuery } from 'querystring';
import { getLocaleFromNextLocale, getRegionFromRouteParam } from 'utils/market';

interface PropTypes extends Pagination {
  canonicalUrl: string;
  mainCategoryFields: IBlogCategoryFields;
  subCategories: Entry<IBlogCategoryFields>;
  blogEntries: ArticleCard[];
}
const PAGE_SIZE = 6;

const SubCategoryPage: NextPage<PropTypes> = ({
  mainCategoryFields,
  subCategories,
  blogEntries,
  pages,
  canonicalUrl,
}) => {
  const pageNumber = usePageNumber({ fallback: pages.currentPage });
  return (
    <>
      <MagazineHead
        canonicalUrl={canonicalUrl}
        pageTitle={subCategories.fields.pageTitle}
        metaDescription={subCategories.fields.metaDescription}
        metaRobots={subCategories.fields.metaRobots}
        mainCategory={mainCategoryFields}
        subCategory={subCategories.fields}
      />
      <SubCategory
        mainCategoryFields={mainCategoryFields}
        subCategories={subCategories.fields}
        blogEntries={blogEntries.slice((pageNumber - 1) * PAGE_SIZE, pageNumber * PAGE_SIZE)}
        pages={{ ...pages, currentPage: pageNumber }}
      />
    </>
  );
};

interface UrlQuery extends ParsedUrlQuery, BathUrlQuery {
  region: Region;
  mainCategory: string;
  subCategory: string;
}
export const getStaticProps: GetStaticProps<PropTypes, UrlQuery> = async (context) => {
  const region = getRegionFromRouteParam(context.params?.region);
  const locale = getLocaleFromNextLocale(context.params?.region, context.locale);
  const { magazineBasePath } = getRegionConfig(region);
  if (!magazineBasePath || context.params?.magazine !== magazineBasePath)
    return { notFound: true, revalidate: true };
  const index = await fetchMagazineIndex();
  const { articles, categories } = await getMagazineData(index, locale, region);
  const mainCategory = categories.find(
    ({ fields, handle }) => fields.isMainCategory && handle === context.params?.mainCategory
  );
  const subCategory = categories.find(
    ({ sys, handle }) =>
      handle === context.params?.subCategory &&
      mainCategory?.fields.childCategories?.some((child) => child.sys.id === sys.id)
  );
  if (!mainCategory || !subCategory) return { notFound: true, revalidate: true };
  const matching = articles.filter(
    ({ fields }) => fields.subCategory?.sys.id === subCategory.sys.id
  );
  return {
    props: {
      canonicalUrl: getMagazineCanonicalUrl({ index, region, locale, mainCategory, subCategory }),
      mainCategoryFields: mainCategory.fields,
      subCategories: subCategory,
      blogEntries: getArticleCards(matching).map(({ fields }) => fields),
      pages: {
        currentPage: 1,
        total: matching.length,
        totalPages: Math.max(1, Math.ceil(matching.length / PAGE_SIZE)),
      },
    },
    revalidate: 3600,
  };
};

export const getStaticPaths: GetStaticPaths<UrlQuery> = async () => ({
  paths: [],
  fallback: 'blocking',
});
export default SubCategoryPage;
